"use client";

import { motion } from "framer-motion";
import { History, Laptop, Smartphone, Trash2 } from "lucide-react";

import { useSecurityMockContent } from "@/hooks/security/useSecurityMockContent";
import { cn } from "@/lib/utils";

const DEVICE_ICONS = [Laptop, Smartphone] as const;

export const SessionsVisual = () => {
  const {
    sessionDevices,
    sessionCurrentLabel,
    sessionSignOutOthersLabel,
    sessionViewHistoryLabel,
    sessionRevokeLabel,
  } = useSecurityMockContent();

  const hasOtherSessions = sessionDevices.some((device) => !device.current);

  return (
    <div className="flex w-full flex-col gap-2">
      {hasOtherSessions ? (
        <div className="flex justify-end">
          <span className="rounded-lg border border-zinc-200/60 bg-white px-2.5 py-1 text-[10px] font-medium text-zinc-600 dark:border-white/[0.08] dark:bg-[#14161c] dark:text-white/70 sm:text-[11px]">
            {sessionSignOutOthersLabel}
          </span>
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        {sessionDevices.map(({ key, device, signedIn, current }, index) => {
          const Icon = DEVICE_ICONS[index] ?? Laptop;

          return (
            <motion.div
              key={key}
              className="flex items-center"
              initial={
                current ? false : { opacity: 0, x: index % 2 === 0 ? -6 : 6 }
              }
              whileInView={current ? undefined : { opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-xl border px-3 py-2.5",
                  current
                    ? "border-emerald-400/30 bg-emerald-400/[0.04] dark:bg-emerald-400/[0.06]"
                    : "border-zinc-200/50 bg-white dark:border-white/[0.05] dark:bg-[#14161c]",
                )}
              >
                <div
                  className={cn(
                    "flex size-8 shrink-0 items-center justify-center rounded-lg",
                    current
                      ? "bg-emerald-500/15"
                      : "bg-zinc-100 dark:bg-white/[0.06]",
                  )}
                >
                  <Icon
                    className={cn(
                      "size-4",
                      current
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-zinc-500 dark:text-white/60",
                    )}
                    aria-hidden
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-xs font-semibold text-zinc-800 dark:text-white/90 sm:text-[13px]">
                      {device}
                    </p>
                    {current ? (
                      <span className="shrink-0 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 sm:text-[11px]">
                        {sessionCurrentLabel}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-[11px] leading-snug text-zinc-500 dark:text-white/50">
                    {signedIn}
                  </p>
                </div>
                {!current ? (
                  <div className="flex shrink-0 items-center gap-1">
                    <span
                      className="flex size-7 items-center justify-center rounded-md text-zinc-400 dark:text-white/45"
                      aria-label={sessionViewHistoryLabel}
                      title={sessionViewHistoryLabel}
                    >
                      <History className="size-3.5" aria-hidden />
                    </span>
                    <span
                      className="flex size-7 items-center justify-center rounded-md text-zinc-400 dark:text-white/45"
                      aria-label={sessionRevokeLabel}
                      title={sessionRevokeLabel}
                    >
                      <Trash2 className="size-3.5" aria-hidden />
                    </span>
                  </div>
                ) : null}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};
